import React, { useState } from 'react'
import { Upload, message, Image, Spin } from 'antd'
import { Plus, X, ChevronLeft, ChevronRight } from 'lucide-react'
import './ImageUpload.css'

export interface ImageUploadProps {
  value?: string[]
  onChange?: (urls: string[]) => void
  maxCount?: number
  uploadFn?: (file: File) => Promise<string>
  actionUrl?: string
  className?: string
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  value = [],
  onChange,
  maxCount = 5,
  uploadFn,
  actionUrl = '/upload/image',
  className = '',
}) => {
  const [loading, setLoading] = useState(false)

  const customRequest = async (options: any) => {
    const { file, onSuccess, onError } = options
    setLoading(true)

    try {
      let url = ''
      if (uploadFn) {
        url = await uploadFn(file)
      } else {
        const formData = new FormData()
        formData.append('image', file)
        const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null
        const res = await fetch(actionUrl, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        })
        if (!res.ok) {
          throw new Error(`上传失败 (${res.status})`)
        }
        const json = await res.json()
        url = json?.data?.url || json?.url || ''
        if (!url) {
          throw new Error(json?.message || '未能解析上传图片地址')
        }
      }

      const newImages = [...(value || []), url]
      onChange?.(newImages)
      onSuccess?.({ url })
      message.success('图片上传成功')
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error)
      message.error(msg || '上传失败')
      onError?.(error)
    } finally {
      setLoading(false)
    }
  }

  const handleRemove = (url: string) => {
    const newImages = (value || []).filter((item) => item !== url)
    onChange?.(newImages)
  }

  const moveImage = (index: number, direction: number) => {
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= (value || []).length) return
    const newImages = [...(value || [])]
    const temp = newImages[index]
    newImages[index] = newImages[newIndex]
    newImages[newIndex] = temp
    onChange?.(newImages)
  }

  return (
    <div className={`admin-image-uploader cstore-image-uploader ${className}`}>
      <div className="image-grid">
        {(value || []).map((url, index) => (
          <div key={`${url}-${index}`} className="image-card">
            <Image src={url} className="uploaded-img" preview={{ src: url }} />
            <button
              type="button"
              className="remove-btn"
              onClick={() => handleRemove(url)}
              title="删除图片"
            >
              <X size={12} />
            </button>
            <div className="reorder-bar">
              {index > 0 && (
                <button
                  type="button"
                  className="reorder-btn"
                  onClick={() => moveImage(index, -1)}
                  title="向前移动"
                >
                  <ChevronLeft size={12} />
                </button>
              )}
              {index < (value || []).length - 1 && (
                <button
                  type="button"
                  className="reorder-btn"
                  onClick={() => moveImage(index, 1)}
                  title="向后移动"
                >
                  <ChevronRight size={12} />
                </button>
              )}
            </div>
          </div>
        ))}

        {(value || []).length < maxCount && (
          <Upload
            accept="image/*"
            showUploadList={false}
            customRequest={customRequest}
          >
            <div className="upload-trigger">
              {loading ? (
                <Spin size="small" />
              ) : (
                <>
                  <Plus size={20} className="upload-icon" />
                  <span className="upload-text">上传图片</span>
                </>
              )}
            </div>
          </Upload>
        )}
      </div>
      <div className="upload-hint">
        最多上传 {maxCount} 张图片，首张作为主图展示。支持 PNG、JPG、WEBP 格式。
      </div>
    </div>
  )
}

export default ImageUpload
